import React, { useState, useRef, useEffect } from 'react';
import http from 'src/services/httpService';
import Button from '../button';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Form } from '../../utils/form';
import { KEYS } from '../../utils/keydown-decorator';
import Dialog from '../../componentes/dialog';
import Drawer from '../drawer/index.js';
import cloneDeep from 'lodash/cloneDeep';
import { capitalizeFirstLetter } from '../../utils/string';
import { getZIndexAboveDialog } from '../../utils/zIndexOverlay';
import focusManager from '../../utils/focus-manager';
import Input from '../input';
import { TIPO_CAMPO_CUSTOMIZADO } from 'src/utils/constants';
import { SearchQueryContext } from './SearchQueryContext';

const mergeFilteredVlrs = (
  currentFilter,
  listaCamposCustomizados,
  campoModel
) => {
  const currentFilterClone = cloneDeep(currentFilter);
  const listaCamposCustomizadosClone = cloneDeep(listaCamposCustomizados || []);

  try {
    const valoresAtuais = currentFilterClone?.valor?.[campoModel];
    if (!Array.isArray(valoresAtuais)) {
      return listaCamposCustomizadosClone;
    }

    for (let i = 0; i < valoresAtuais.length; i++) {
      const e = valoresAtuais[i];
      if (!e?.campo?.id) {
        continue;
      }
      const campoCustomizado = listaCamposCustomizadosClone.find(
        (l) => l.idCampo == e.campo.id
      );
      if (campoCustomizado) {
        campoCustomizado.campo.valores = e.campo.valores;
        campoCustomizado.valor = e.valor;
      }
    }
  } catch (err) {
    console.error(err);
  }

  return listaCamposCustomizadosClone;
};

function FiltroDrawerNew(props) {
  const {
    searchComponent,
    model,
    tipoLayout,
    handleClose,
    isOpen,
    tipoItem,
    tipoItensCamposCustomizados,
    enableSearch = false,
    ...rest
  } = props;

  const intl = useIntl();
  const filter = useSelector((state) => state.filter);

  const other = { ...rest, filter };

  const [currentFilter, setCurrentFilter] = useState(() =>
    props.modelValue && !(props.selectedFilter && props.selectedFilter.valor)
      ? props.modelValue
      : cloneDeep(props.selectedFilter)
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [zIndexCalculado] = useState(() => getZIndexAboveDialog());
  const [focusTrapPriorityCalculada] = useState(
    () => focusManager.getTopPriority() + 0.5
  );
  const [, forceUpdate] = useState(0);
  const componentAdapter = useRef({
    state: {},
    setState(updater) {
      const partial =
        typeof updater === 'function' ? updater(this.state) : updater;
      Object.assign(this, { state: { ...this.state, ...partial } });
      forceUpdate((n) => n + 1);
    },
  });

  const formRef = useRef(null);
  if (!formRef.current) {
    let modelInicial;
    if (props.modelValue) {
      modelInicial =
        props.modelValue &&
        !(props.selectedFilter && props.selectedFilter.valor)
          ? props.modelValue
          : props.selectedFilter && cloneDeep(props.selectedFilter.valor);
    } else {
      modelInicial = props.selectedFilter ? props.defaultFilter : {};
    }

    formRef.current = new Form({
      component: componentAdapter.current,
      schema: props.searchSchema,
      model: modelInicial
        ? modelInicial
        : props.modelValue &&
          !(props.selectedFilter && props.selectedFilter.valor)
        ? props.modelValue
        : props.selectedFilter && cloneDeep(props.selectedFilter.valor),
    });
  }

  const form = formRef.current;
  const dialogRef = useRef(null);

  /*Retorna as configurações de campos customizados que devem ser carregadas.
   * Novo formato:
   * tipoItensCamposCustomizados=
   * {
   *  [
   *   {
   *     tipoItem: ENTIDADES_CUSTOMIZADAS.AUDITORIA_RISCO,
   *     campoModel: 'camposCustomizados',
   *   },
   *   {
   *    tipoItem: ENTIDADES_CUSTOMIZADAS.PONTO_AUDITORIA,
   *    campoModel: 'camposCustomizadosPontoAuditoria',
   *   },
   *  ]
   * }
   * Compatibilidade com o formato antigo:
   * tipoItem={ENTIDADES_CUSTOMIZADAS.AUDITORIA_RISCO}
   * Nesse caso, será criado automaticamente:
   * { tipoItem: tipoItem, campoModel: 'camposCustomizados' }
   */
  const obterConfiguracoesCamposCustomizados = () => {
    if (
      Array.isArray(tipoItensCamposCustomizados) &&
      tipoItensCamposCustomizados.length > 0
    ) {
      return tipoItensCamposCustomizados
        .filter((configuracao) => configuracao?.tipoItem)
        .map((configuracao) => ({
          ...configuracao,
          campoModel: configuracao.campoModel || 'camposCustomizados',
        }));
    }
    if (tipoItem) {
      return [{ tipoItem, campoModel: 'camposCustomizados' }];
    }
    return [];
  };

  const obterTipoItemCampoCustomizado = (campoCustomizado) => {
    return (
      campoCustomizado?.tipoItem || campoCustomizado?.campo?.tipoItem || null
    );
  };

  const listaPertenceAoTipoItem = (lista, tipoItemEsperado) => {
    if (!Array.isArray(lista) || lista.length === 0) {
      return true;
    }
    const tipoItemAtual = obterTipoItemCampoCustomizado(lista[0]);
    return !tipoItemAtual || tipoItemAtual === tipoItemEsperado;
  };

  /* Obtém os campos customizados de um ou vários tipos de entidade. 
   A configuração define: 
   - tipoItem: entidade para buscar os campos 
   - campoModel: propriedade onde os campos são armazenados no model 
   Isso permite que o componente seja reutilizado por qualquer tela sem conhecer nomes específicos*/
  const obterCamposCustomizadosMultiplosItens = async (currentFilterLocal) => {
    const configuracoes = obterConfiguracoesCamposCustomizados();
    if (configuracoes.length === 0) {
      setCurrentFilter({ ...currentFilterLocal, valor: props.search });
      return;
    }
    try {
      const resultados = await Promise.all(
        configuracoes.map(async (configuracao) => {
          const response = await http.post(
            `/CampoCustomizado/ObterListaCamposCustomizados`,
            { tipoItem: configuracao.tipoItem }
          );
          return { ...configuracao, camposCustomizados: response.data || [] };
        })
      );

      const novoValor = { ...(currentFilterLocal?.valor || {}) };

      resultados.forEach((resultado) => {
        const { tipoItem, campoModel, camposCustomizados } = resultado;
        const modelCampoCustomizado = form.getModel(campoModel);
        if (!modelCampoCustomizado) {
          return;
        }
        if (camposCustomizados.length > 0) {
          const listaAtual = modelCampoCustomizado.value;

          if (!listaPertenceAoTipoItem(listaAtual, tipoItem)) {
            novoValor[campoModel] = null;
            modelCampoCustomizado.requestChange(null);
            return;
          }
          const lista = mergeFilteredVlrs(
            currentFilterLocal,
            camposCustomizados,
            campoModel
          );
          novoValor[campoModel] = lista;
          modelCampoCustomizado.requestChange(lista);
        } else {
          novoValor[campoModel] = null;
          modelCampoCustomizado.requestChange(null);
        }
      });
      setCurrentFilter({ ...currentFilterLocal, valor: novoValor });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (props.ignorarCamposCustomizados) {
      setCurrentFilter({ ...currentFilter, valor: props.search });
      return;
    }

    const filtroInicial =
      currentFilter?.valor != null
        ? currentFilter
        : { ...(currentFilter || {}), valor: props.search || {} };
    obterCamposCustomizadosMultiplosItens(filtroInicial);
  }, []);

  const handleSaveFilter = async () => {
    if (props.onChangeUrl && props.modelValue) {
      props.onChangeUrl(form.getValue());
    } else {
      const novoFiltro = {
        ...cloneDeep(currentFilter),
        valor: form.getValue(),
      };

      if (props.usarFiltroSalvo) {
        await http
          .post(`/FiltroColaborador/${props.filtroId}/SalvarFiltro`, {
            ...novoFiltro,
            filtroId: props.filtroId,
          })
          .then((response) => {
            const url = new URL(window.location.href);

            if (url.searchParams.has('filtroFavoritoId')) {
              url.searchParams.delete('filtroFavoritoId');
              window.location.replace(url.toString());
            }

            props.onSearch && props.onSearch(response.data);
          })
          .catch(() => {});
      } else {
        const result = await props.onSearch?.(form.getValue(), true);
        if (result === false) return;
      }
    }

    if (props.tipoLayout === 'drawer') {
      props.handleClose && props.handleClose();
    } else {
      dialogRef.current && dialogRef.current.close();
    }
  };

  const handleSaveFilterRef = useRef(handleSaveFilter);
  handleSaveFilterRef.current = handleSaveFilter;

  useEffect(() => {
    const keyDownListener = (e) => {
      const keyCode = e.which || e.keyCode;
      if (
        e.altKey &&
        keyCode === KEYS.ENTER &&
        !(e.target.tagName === 'TEXTAREA' && keyCode === KEYS.ENTER)
      ) {
        handleSaveFilterRef.current(e);
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', keyDownListener);
    return () => document.removeEventListener('keydown', keyDownListener);
  }, []);

  const limparCamposCustomizados = (campoModel) => {
    const modelCampoCustomizado = form.getModel(campoModel);
    if (!modelCampoCustomizado) {
      return;
    }
    const array = modelCampoCustomizado.value;
    if (!Array.isArray(array)) {
      modelCampoCustomizado.requestChange(null);
      return;
    }
    const newList = Object.assign([], array);
    newList.forEach((campoCustomizado) => {
      if (!campoCustomizado?.campo) {
        return;
      }
      if (campoCustomizado.campo.multiSelecao) {
        campoCustomizado.campo.valores = null;
      } else if (
        campoCustomizado.campo.tipo?.id ===
        TIPO_CAMPO_CUSTOMIZADO.VALOR_TABELADO
      ) {
        campoCustomizado.valor = null;
      } else {
        campoCustomizado.valor = '';
      }
    });
    modelCampoCustomizado.requestChange(newList);
  };

  const handleClearFilter = () => {
    const fields = form && form.getValue();
    if (!fields) {
      return;
    }
    if (props.getClearedFilter) {
      form.setValue(null, props.getClearedFilter(fields));
      return;
    }
    const configuracoes = obterConfiguracoesCamposCustomizados();
    const camposCustomizados = new Set(
      configuracoes.map((configuracao) => configuracao.campoModel)
    );
    for (const item in fields) {
      const campo = form.getModel(item);
      if (!campo) {
        continue;
      }
      if (campo.value === true || campo.value === false) {
        form.setValue(item, false);
      } else if (camposCustomizados.has(item)) {
        limparCamposCustomizados(item);
      } else {
        form.setValue(item, null);
      }
    }
  };

  const actions = [
    <Button
      key="btnFiltrar"
      onClick={handleSaveFilter}
      className="pull-right"
      leftIcon="magnify"
    >
      {intl.formatMessage({ id: 'label.filtrar' })}
    </Button>,
  ];

  const footerButton = (
    <div className="pt-1">
      <a className="text-primary" onClick={handleClearFilter}>
        {intl.formatMessage({ id: 'limparTodos' })}
      </a>
    </div>
  );

  let modelDefault = form.getModel();

  if (props.useFieldModels && props.searchSchema) {
    modelDefault = {};
    const schemaFields = props.searchSchema.fields || {};
    Object.keys(schemaFields).forEach((key) => {
      modelDefault[key] = form.getModel(key);
    });
  }

  const content = enableSearch ? (
    <SearchQueryContext.Provider value={{ searchQuery, intl }}>
      <div>
        <div style={{ marginBottom: 20 }}>
          <Input
            model={{
              label: null,
              value: searchQuery,
              requestChange: (value) => {
                setSearchQuery(value);
              },
            }}
            placeholder={intl.formatMessage({ id: 'labelPesquisarFiltros' })}
            showLabel={false}
            style={{ width: '100%' }}
          />
        </div>
        {searchComponent &&
          React.cloneElement(searchComponent, {
            ...other,
            tipoItemId: tipoItem,
            model: model ? model : modelDefault,
          })}
      </div>
    </SearchQueryContext.Provider>
  ) : (
    <div>
      {searchComponent &&
        React.cloneElement(searchComponent, {
          ...other,
          tipoItemId: tipoItem,
          model: model ? model : modelDefault,
        })}
    </div>
  );

  if (tipoLayout === 'drawer') {
    const drawerActions = (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <span
          style={{
            color: '#0D7E94',
            cursor: 'pointer',
            fontSize: 16,
            fontWeight: 'bold',
          }}
          onClick={handleClearFilter}
        >
          {intl.formatMessage({ id: 'limparTodos' })}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            leftIcon="magnify"
            style={{ width: '5rem' }}
            color="primary"
            onClick={handleSaveFilter}
          >
            {intl.formatMessage({ id: 'label.filtrar' })}
          </Button>
        </div>
      </div>
    );

    return (
      <Drawer
        open={isOpen}
        close={handleClose}
        zIndex={props.zIndex ?? zIndexCalculado}
        actions={drawerActions}
        title={
          <div
            className="font-weight-bold pt-4 pb-4"
            style={{ fontSize: '1.5rem', color: '#404040', fontWeight: 700 }}
          >
            {capitalizeFirstLetter(intl.formatMessage({ id: 'filtros' }))}
          </div>
        }
        padding={true}
        headerStyle={{
          borderBottom: '1px solid #e4eaec',
          paddingRight: 20,
          paddingLeft: 20,
          paddingTop: 0,
          paddingBottom: 0,
        }}
        bodyStyle={{ overflowY: 'auto', overflowX: 'hidden' }}
        actionsStyle={{ borderTop: '1px solid #e4eaec', padding: 20 }}
        focusTrap={props.focusTrap ?? true}
        focusTrapPriority={
          props.focusTrapPriority ?? focusTrapPriorityCalculada
        }
      >
        <div>{content}</div>
      </Drawer>
    );
  }

  return (
    <Dialog
      title={intl.formatMessage({ id: 'filtros' })}
      ref={dialogRef}
      {...other}
      actions={actions}
      footer={footerButton}
    >
      {content}
    </Dialog>
  );
}

export default FiltroDrawerNew;
